import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminHeader from '../components/AdminHeader';
import ConfirmModal from '../components/ConfirmModal';
import axios from 'axios';
import { escapeCSV, downloadCSV } from '../utils/csvExport';
import './AdminDeliveryPage_Mobile.css';

// Default provinces/cities (stable reference to satisfy hooks)
const DEFAULT_PROVINCES = [
  { _id: 'CAVITE', name: 'CAVITE', cities: ['Silang','Tagaytay City','Dasmariñas City','General Trias City','Imus City','Bacoor City','Carmona','Amadeo','Tanza','Trece Martires City','Indang','Alfonso','Mendez-Nuñez','Magallanes','Maragondon','Naic'] },
  { _id: 'LAGUNA', name: 'LAGUNA', cities: ['Santa Rosa City','Biñan City','Cabuyao City','Calamba City','San Pedro City','Los Baños'] },
  { _id: 'METRO MANILA (NCR)', name: 'METRO MANILA (NCR)', cities: ['Muntinlupa City','Parañaque City','Las Piñas City','Taguig City','Pasay City'] },
  { _id: 'BATANGAS PROVINCE', name: 'BATANGAS PROVINCE', cities: ['Sto. Tomas City','Tanauan City','Malvar','Lipa City'] },
  { _id: 'RIZAL PROVINCE', name: 'RIZAL PROVINCE', cities: ['Antipolo City','Taytay','Cainta'] }
];

function AdminDeliveryPage() {
  const [zones, setZones] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [form, setForm] = useState({ province: '', city: '', fee: 0, available: true });
  const [newProvinceName, setNewProvinceName] = useState('');
  const [addCityProvince, setAddCityProvince] = useState('');
  const [addCityName, setAddCityName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProvinceModal, setShowProvinceModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [deleteZoneId, setDeleteZoneId] = useState(null);
  const [deleteProvinceId, setDeleteProvinceId] = useState(null);
  const [provinceFilter, setProvinceFilter] = useState(null); // null = all provinces
  

  const fetchZones = useCallback(async () => {
    try {
      const t = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API}/api/delivery`, { headers: { Authorization: `Bearer ${t}` } });
      setZones(res.data || []);
    } catch (err) {
      console.error('Error loading zones', err);
      setZones([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProvinces = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/api/delivery/provinces`);
      const list = res.data || [];
      // Merge defaults with DB list, de-duplicating by normalized name and union cities
      const norm = s => (s || '').toString().trim().toLowerCase();
      const map = new Map();
      // seed with DB items first (authoritative _id)
      for (const p of list) {
        map.set(norm(p.name), { ...p, cities: Array.isArray(p.cities) ? [...p.cities] : [], __source: 'db' });
      }
      // merge defaults, union cities when same name
      for (const d of DEFAULT_PROVINCES) {
        const key = norm(d.name);
        if (map.has(key)) {
          const existing = map.get(key);
          const citySet = new Set([...(existing.cities || []), ...(d.cities || [])]);
          map.set(key, { ...existing, cities: Array.from(citySet) });
        } else {
          map.set(key, { ...d, __source: 'default' });
        }
      }
      const merged = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
      setProvinces(merged);
      // seed form province if not already set
      const seedList = merged.length > 0 ? merged : DEFAULT_PROVINCES;
      if (seedList.length > 0) {
        const p = seedList[0];
        setForm(prev => (prev.province ? prev : { ...prev, province: p.name, city: p.cities[0] || '' }));
        setAddCityProvince(prev => prev || p.name);
      }
    } catch (err) {
      console.error('Error loading provinces', err);
      setProvinces(DEFAULT_PROVINCES);
    }
  }, []);

    useEffect(() => { fetchZones(); fetchProvinces(); }, [fetchZones, fetchProvinces]);

  const handleSaveZone = async (e) => {
    e.preventDefault();
    setShowSaveConfirmModal(true);
  };

  const confirmSaveZone = async () => {
    try {
      const t = localStorage.getItem('token');
      const normalize = s => (s || '').normalize('NFC').trim().toLowerCase();
      const existing = zones.find(z => normalize(z.province) === normalize(form.province) && normalize(z.city) === normalize(form.city));
      if (existing) {
        // Update via PUT
        await axios.put(`${process.env.REACT_APP_API}/api/delivery/${existing._id}`,
          { fee: form.fee, available: form.available, note: existing.note },
          { headers: { Authorization: `Bearer ${t}` } }
        );
      } else {
        // Create via POST
        await axios.post(`${process.env.REACT_APP_API}/api/delivery`, form, { headers: { Authorization: `Bearer ${t}` } });
      }
      setForm({ province: provinces[0]?.name || '', city: provinces[0]?.cities?.[0] || '', fee: 0, available: true });
      fetchZones();
      setShowSaveConfirmModal(false);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save';
      if (/already exists/i.test(msg)) {
        alert('Delivery zone already exists. Fee updated instead.');
      } else {
        alert(msg);
      }
      setShowSaveConfirmModal(false);
    }
  };

  const handleDeleteZone = async (id) => {
    setDeleteZoneId(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteZone = async () => {
    if (!deleteZoneId) return;
    try {
      const t = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API}/api/delivery/${deleteZoneId}`, { headers: { Authorization: `Bearer ${t}` } });
      fetchZones();
    } catch (err) { 
      console.error('Failed to delete zone:', err);
    }
    setDeleteZoneId(null);
  };

  const handleCreateProvince = async () => {
    const name = (newProvinceName || '').trim();
    if (!name) {
      setShowProvinceModal(true);
      return;
    }
    try {
      const t = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API}/api/delivery/provinces`, { name, cities: [] }, { headers: { Authorization: `Bearer ${t}` } });
      setNewProvinceName('');
      fetchProvinces();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create province');
    }
  };

  // Separate: Add City to existing province (explicit)
  const handleAddCityOnly = async () => {
    const provinceName = (addCityProvince || '').trim();
    const city = (addCityName || '').trim();
    if (!provinceName || !city) return alert('Select a province and enter a city');
    try {
      const t = localStorage.getItem('token');
      // find province in current list
      const p = provinces.find(x => x.name === provinceName);
      let provinceId = p?._id;
      // If province is from defaults (no DB id), create it first
      if (!provinceId || typeof provinceId !== 'string' || provinceId.toString().length < 12) {
        const createRes = await axios.post(`${process.env.REACT_APP_API}/api/delivery/provinces`, { name: provinceName, cities: [] }, { headers: { Authorization: `Bearer ${t}` } });
        provinceId = createRes.data?._id;
      }
      const updatedCities = Array.from(new Set([...(p?.cities || []), city]));
      await axios.put(`${process.env.REACT_APP_API}/api/delivery/provinces/${provinceId}`, { cities: updatedCities }, { headers: { Authorization: `Bearer ${t}` } });
      setAddCityName('');
      fetchProvinces();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add city');
    }
  };

  const exportToCSV = () => {
    let csvContent = 'Province,City,Delivery Fee,Available\n';
    
    zones.forEach(zone => {
      const province = escapeCSV(zone.province || '');
      const city = escapeCSV(zone.city || '');
      const fee = escapeCSV(zone.fee || 0);
      const available = escapeCSV(zone.available ? 'Yes' : 'No');
      
      csvContent += `${province},${city},${fee},${available}\n`;
    });
    
    const filename = `delivery_zones_backup_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  const handleDeleteProvince = async (id) => {
    setDeleteProvinceId(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteProvince = async () => {
    if (!deleteProvinceId) return;
    try {
      const t = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API}/api/delivery/provinces/${deleteProvinceId}`, { headers: { Authorization: `Bearer ${t}` } });
      fetchProvinces();
    } catch (err) { 
      console.error('Failed to delete province:', err);
    }
    setDeleteProvinceId(null);
  };

  if (loading) {
    return (
      <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Green Header Bar */}
        <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
        
        {/* Navbar */}
        <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
          <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
            <AdminHeader />
          </div>
        </div>

        {/* Loading State */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, rotate: 360 }}
            transition={{ duration: 0.6, rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
            style={{ fontSize: '4rem' }}
          >
            🌱
          </motion.div>
        </div>
      </div>
    );
  }

  // Derived filtered zones based on provinceFilter
  const filteredZones = provinceFilter ? zones.filter(z => z.province === provinceFilter) : zones;

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
      
      {/* Navbar */}
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>

      <div style={{ width: '100%', margin: '40px auto', padding: '0 20px', flex: 1, boxSizing: 'border-box' }}>
        <div className="admin-delivery-page">
          {/* Header */}
          <motion.div
            className="delivery-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div>
              <h1>🚚 Delivery Zones & Fees</h1>
              <p className="delivery-subtitle">Manage delivery zones, provinces, and cities</p>
            </div>
            <div className="header-actions">
              <motion.button
                onClick={exportToCSV}
                className="btn-export-csv"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>📊</span>
                Export CSV
              </motion.button>
            </div>
          </motion.div>

          {/* Province & City Management Section - Side by Side */}
          <div className="province-city-grid">
            <motion.div
              className="section-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h2 className="section-title">
                <span className="section-title-icon">🗺️</span>
                Create Province
              </h2>
              
              <div className="province-form">
                <div className="form-group">
                  <label className="form-label required">Province Name</label>
                  <input
                    className="form-input"
                    value={newProvinceName}
                    onChange={e => setNewProvinceName(e.target.value)}
                    placeholder="e.g., CAVITE"
                  />
                </div>

                <motion.button
                  className="btn-primary"
                  onClick={handleCreateProvince}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ width: '100%', marginTop: '0.5rem' }}
                >
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  Create Province
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              className="section-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <h2 className="section-title">
                <span className="section-title-icon">🏙️</span>
                Add City to Province
              </h2>
              <div className="province-form">
                <div className="form-group">
                  <label className="form-label required">Province</label>
                  <select
                    className="form-select"
                    value={addCityProvince}
                    onChange={e => setAddCityProvince(e.target.value)}
                  >
                    {provinces.map(p => (
                      <option key={p._id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label required">City Name</label>
                  <input
                    className="form-input"
                    value={addCityName}
                    onChange={e => setAddCityName(e.target.value)}
                    placeholder="e.g., Silang"
                    onKeyPress={e => {
                      if (e.key === 'Enter') { e.preventDefault(); handleAddCityOnly(); }
                    }}
                  />
                </div>
                <motion.button
                  className="btn-secondary"
                  onClick={handleAddCityOnly}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ width: '100%', marginTop: '0.5rem' }}
                >
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                  Add City
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Zone Creation Form */}
          <motion.div
            className="section-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <h2 className="section-title">
              <span className="section-title-icon">➕</span>
              Add Delivery Zone
            </h2>
            
            <form className="zone-form" onSubmit={handleSaveZone}>
              <div className="zone-form-grid">
                <div className="form-group">
                  <label className="form-label required">Province</label>
                  <select
                    className="form-select"
                    value={form.province}
                    onChange={e => {
                      const p = e.target.value;
                      const found = provinces.find(x => x.name === p);
                      setForm(prev => ({ ...prev, province: p, city: found?.cities?.[0] || '' }));
                    }}
                    required
                  >
                    <option value="">-- Select province --</option>
                    {provinces.map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label required">City</label>
                  <select
                    className="form-select"
                    value={form.city}
                    onChange={e => setForm({ ...form, city: e.target.value })}
                    required
                  >
                    {(provinces.find(p => p.name === form.province)?.cities || []).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label required">Delivery Fee (₱)</label>
                  <input
                    className="form-input"
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.fee}
                    onChange={e => setForm({ ...form, fee: Number(e.target.value) })}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Availability</label>
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={form.available}
                      onChange={e => setForm({ ...form, available: e.target.checked })}
                    />
                    <span className="checkbox-label">Available for delivery</span>
                  </div>
                </div>
              </div>

              <motion.button
                type="submit"
                className="btn-primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{ marginTop: '12px' }}
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                Save Delivery Zone
              </motion.button>
            </form>
          </motion.div>

          {/* Zones Table/Cards */}
          {filteredZones.length === 0 ? (
            <motion.div
              className="empty-state"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="empty-state-icon">📦</div>
              <h3 className="empty-state-title">No Delivery Zones{provinceFilter ? ` for ${provinceFilter}` : ''} Found</h3>
              <p className="empty-state-text">{provinceFilter ? 'Try selecting a different province or clear the filter.' : 'Create your first delivery zone to start offering delivery services'}</p>
            </motion.div>
          ) : (
            <>
              {/* Province Filter Chips */}
              <motion.div
                className="province-filter"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: 0.25 }}
              >
                <div className="province-filter-label">Filter by Province:</div>
                <div className="province-filter-chips" role="group" aria-label="Filter delivery zones by province">
                  <button
                    type="button"
                    className={`province-chip ${provinceFilter === null ? 'active' : ''}`}
                    onClick={() => setProvinceFilter(null)}
                    aria-label="Show all provinces"
                  >All</button>
                  {provinces.map(p => (
                    <button
                      key={p._id}
                      type="button"
                      className={`province-chip ${provinceFilter === p.name ? 'active' : ''}`}
                      onClick={() => setProvinceFilter(prev => prev === p.name ? null : p.name)}
                      aria-label={`Filter province ${p.name}`}
                    >{p.name}</button>
                  ))}
                </div>
              </motion.div>

              {/* Desktop Table View */}
              <motion.div
                className="zones-table-container desktop-only"
                initial={false}
                animate={{ opacity: 1 }}
              >
                <table className="zones-table">
                  <thead>
                    <tr>
                      <th>Province</th>
                      <th>City</th>
                      <th>Delivery Fee</th>
                      <th>Availability</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredZones.map((z) => (
                      <motion.tr
                        key={z._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.18 }}
                      >
                          <td><span className="zone-province">{z.province}</span></td>
                          <td><span className="zone-city">{z.city}</span></td>
                          <td><span className="zone-fee">₱{(z.fee || 0).toFixed(2)}</span></td>
                          <td>
                            <span className={`availability-badge ${z.available ? 'available' : 'unavailable'}`}>
                              {z.available ? '✓ Available' : '✕ Unavailable'}
                            </span>
                          </td>
                          <td>
                            <motion.button
                              onClick={() => handleDeleteZone(z._id)}
                              className="btn-delete"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg>
                              Delete
                            </motion.button>
                          </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>

              {/* Mobile Card View */}
              <motion.div
                className="zones-grid mobile-only"
                initial={false}
                animate={{ opacity: 1 }}
              >
                {filteredZones.map((z) => (
                  <motion.div
                    key={z._id}
                    className="zone-card"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.18 }}
                  >
                      <div className="zone-card-header">
                        <div className="zone-location">
                          <h3>{z.province}</h3>
                          <p>📍 {z.city}</p>
                        </div>
                        <div className="zone-fee-display">
                          <div className="zone-fee-label">Delivery Fee</div>
                          <div className="zone-fee-amount">₱{(z.fee || 0).toFixed(2)}</div>
                        </div>
                      </div>
                      
                      <div className="zone-card-footer">
                        <span className={`availability-badge ${z.available ? 'available' : 'unavailable'}`}>
                          {z.available ? '✓ Available' : '✕ Unavailable'}
                        </span>
                        <motion.button
                          onClick={() => handleDeleteZone(z._id)}
                          className="btn-card-delete"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg>
                          Delete Zone
                        </motion.button>
                      </div>
                  </motion.div>
                ))}
              </motion.div>
            </>
          )}

          {/* Existing Provinces Section */}
          {provinces.length > 0 && (
            <motion.div
              className="provinces-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <h2 className="section-title">
                <span className="section-title-icon">🗂️</span>
                Existing Provinces ({provinces.length})
              </h2>
              
              <div className="provinces-grid">
                <AnimatePresence mode="popLayout">
                  {provinces.map((p, index) => (
                    <motion.div
                      key={p._id}
                      className="province-card"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2, delay: index * 0.03 }}
                      layout
                    >
                      <div className="province-card-header">
                        <span className="province-icon">🗺️</span>
                        <h3 className="province-name">{p.name}</h3>
                        <span className="cities-count">{p.cities.length} {p.cities.length === 1 ? 'city' : 'cities'}</span>
                      </div>
                      
                      <div className="province-cities">
                        {p.cities.join(', ')}
                      </div>
                      
                      <div className="province-card-actions">
                        <motion.button
                          onClick={() => handleDeleteProvince(p._id)}
                          className="btn-delete-province"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg>
                          Delete Province
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
        <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, letterSpacing: 1 }}>© {new Date().getFullYear()} Chiqui's Garden</span>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteZoneId(null);
          setDeleteProvinceId(null);
        }}
        onConfirm={() => {
          if (deleteZoneId) confirmDeleteZone();
          if (deleteProvinceId) confirmDeleteProvince();
        }}
        title="Confirm Delete"
        message={deleteZoneId ? "Are you sure you want to delete this delivery zone?" : "Are you sure you want to delete this province?"}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      />

      <ConfirmModal
        isOpen={showProvinceModal}
        onClose={() => setShowProvinceModal(false)}
        onConfirm={() => {}}
        title="Province Name Required"
        message="Please enter a province name before creating"
        confirmText="OK"
        cancelText=""
        type="warning"
      />

      <ConfirmModal
        isOpen={showSaveConfirmModal}
        onClose={() => setShowSaveConfirmModal(false)}
        onConfirm={confirmSaveZone}
        title="Confirm Delivery Zone"
        message={`Are you sure you want to ${zones.find(z => z.province === form.province && z.city === form.city) ? 'update' : 'add'} delivery zone for ${form.city}, ${form.province} with fee ₱${form.fee.toFixed(2)} and set availability to ${form.available ? 'Available' : 'Unavailable'}?`}
        confirmText="Confirm"
        cancelText="Cancel"
        type="warning"
      />
    </div>
  );
}

export default AdminDeliveryPage;
